import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { App as AntdApp, Tour } from "antd";
import type { TourProps } from "antd";

import { tourAutoStarts, tourForPath } from "@/content/tourRoutes";
import { useUiStore } from "@/stores/uiStore";

import { tourTarget } from "./tourTarget";

/** How often the auto-run re-checks the DOM for the tour's targets. */
const POLL_MS = 300;

/** How long a run keeps looking before giving up entirely. Lazy route
 *  chunks + data fetches can take seconds on a cold load — the old single
 *  600 ms check silently missed the tour about half the time (F15). */
const GIVE_UP_MS = 6000;

/** A request is not given up before this, however finished the page looks. */
const REQUEST_SETTLE_MS = 1500;

/** What the "?" says when the page's tour has nothing to point at. */
export const NO_TOUR_NOW = "This page has no tour to show right now.";

/** Is the page still drawing itself (a placeholder or a spinner in the content)? */
function pageIsLoading(): boolean {
  return Boolean(document.querySelector("main .ant-skeleton, main .ant-spin-spinning"));
}

function findTarget(name: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-tour="${name}"]`);
}

/** UI-03: is the customer in the middle of something a tour would land on top
 *  of — a drawer, a dialog or a menu is open, or the cursor is in a field? */
function customerIsBusy(): boolean {
  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    (active.matches("input, textarea, select") || active.isContentEditable)
  ) {
    return true;
  }
  return Boolean(
    document.querySelector(
      ".ant-drawer-open, .ant-modal-wrap:not([style*='display: none']), " +
        ".ant-popover:not(.ant-popover-hidden), .ant-dropdown:not(.ant-dropdown-hidden), " +
        ".ant-select-dropdown:not(.ant-select-dropdown-hidden)",
    ),
  );
}

/**
 * Guided tour for the current route: spotlights a real control, says what it
 * is, and moves on with Next.
 *
 * Runs by itself once per list or landing page per customer, and only on a
 * page the customer has not started using; one resource's page, and any later
 * visit, runs it on request. Mounted once in the app shell rather than per
 * page, so a page only has to tag the elements it wants explained with
 * `data-tour`.
 *
 * It never takes a click (UI-02): the dimming does not catch the pointer, and
 * a press anywhere outside the tour's own card closes the tour and still
 * lands on what was pressed.
 */
function ProductTour({
  requestedAt,
  onClose,
}: {
  /** Bumped by the topbar button to (re)start the tour for this route. */
  requestedAt: number | null;
  onClose: () => void;
}) {
  const { pathname } = useLocation();
  const { message } = AntdApp.useApp();
  const completed = useUiStore((s) => s.completedTours);
  const complete = useUiStore((s) => s.completeTour);
  // The running tour and the route it belongs to: a tour never follows the
  // customer to another page (its targets are not there).
  const [running, setRunning] = useState<{
    pathname: string;
    steps: NonNullable<TourProps["steps"]>;
  } | null>(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const tour = tourForPath(pathname);

  // Steps are resolved against the live DOM, not built ahead of time: a step
  // whose target isn't rendered (empty table, admin-only button, a tab the
  // customer hasn't opened) is dropped rather than spotlighting nothing.
  const resolve = useCallback((): TourProps["steps"] => {
    if (!tour) return [];
    return tour.steps
      .filter((step) => findTarget(step.target))
      .map((step) => ({
        title: step.title,
        description: step.description,
        // PG1-F7 (B27): measured inside the window, so the mask never draws a negative size
        target: () => {
          const el = findTarget(step.target);
          return el ? tourTarget(el) : (null as unknown as HTMLElement);
        },
      }));
  }, [tour]);

  const start = useCallback((): boolean => {
    const resolved = resolve();
    if (!resolved?.length) return false;
    setRunning({ pathname, steps: resolved });
    return true;
  }, [resolve, pathname]);

  // Auto-run the first time a customer lands on a list or landing page that
  // has a tour — unless they are already using the page (UI-03): a press or a
  // key since arriving, an open drawer or dialog, or a field with the cursor
  // in it calls this visit's run off. Nothing is marked complete, so the next
  // quiet visit offers it again.
  // Bounded retry instead of a single-shot check: the page's lazy chunk (and
  // its data) may land seconds after the route change, so poll for the
  // `data-tour` targets and fire once they've appeared and settled. Steps
  // whose targets never show up within the window are still dropped (`start`
  // re-resolves against the live DOM); if none appear, the tour never runs.
  useEffect(() => {
    if (!tour || completed.includes(pathname) || !tourAutoStarts(pathname)) return;
    const deadline = Date.now() + GIVE_UP_MS;
    let previousFound = -1;
    const stop = () => {
      clearInterval(timer);
      window.removeEventListener("pointerdown", stop, true);
      window.removeEventListener("keydown", stop, true);
    };
    window.addEventListener("pointerdown", stop, true);
    window.addEventListener("keydown", stop, true);
    const timer = setInterval(() => {
      const found = tour.steps.filter((step) => findTarget(step.target)).length;
      const expired = Date.now() >= deadline;
      const settled =
        found === tour.steps.length || // everything's on the page
        (found > 0 && found === previousFound) || // no new targets since last poll
        (found > 0 && expired); // window over — run with what's there
      if (settled) {
        stop();
        if (!customerIsBusy()) start();
        return;
      }
      if (expired) {
        stop(); // genuinely nothing to spotlight
        return;
      }
      previousFound = found;
    }, POLL_MS);
    // Route change/unmount cancels the pending auto-run.
    return stop;
  }, [pathname, tour, completed, start]);

  // Explicit request from the topbar — replays even if already completed.
  // A request made while the page is still loading waits for its targets as
  // the auto-run does: handed back at once, it was dropped without a word and
  // the button needed a second click after the load.
  //
  // The button always answers (B7, platform test round 4). A tour whose steps
  // all point at controls this page does not have (the instance tour's one
  // step was a button only a cloud with Private access shows) used to wait
  // six seconds and hand the request back in silence: "the ? does nothing".
  // Now the wait ends as soon as the page has stopped loading, and says so.
  // One request is answered once: `answered` keeps a re-run of this effect
  // (the route or the tour changing under a pending request) from starting
  // the tour, or the notice, a second time.
  const request = useRef<{
    at: number;
    pathname: string;
    since: number;
    answered: boolean;
  } | null>(null);
  const messageRef = useRef(message);
  useEffect(() => {
    messageRef.current = message;
  }, [message]);
  useEffect(() => {
    if (requestedAt === null) return;
    if (request.current?.at !== requestedAt) {
      request.current = { at: requestedAt, pathname, since: Date.now(), answered: false };
    }
    const asked = request.current;
    if (asked.answered) return;
    // Asked for on another page: hand the request back, or it would still be
    // pending on the next page and start that page's tour unasked.
    const stale = asked.pathname !== pathname;
    let timer: ReturnType<typeof setTimeout>;
    const attempt = () => {
      if (asked.answered) return;
      if (stale) {
        asked.answered = true;
        onCloseRef.current();
        return;
      }
      if (start()) {
        asked.answered = true;
        return;
      }
      const waited = Date.now() - asked.since;
      if (waited >= GIVE_UP_MS || (waited >= REQUEST_SETTLE_MS && !pageIsLoading())) {
        asked.answered = true;
        // Without an antd <App> above (a bare test render) there is no notice to give.
        messageRef.current.info?.(NO_TOUR_NOW);
        onCloseRef.current();
        return;
      }
      timer = setTimeout(attempt, POLL_MS);
    };
    timer = setTimeout(attempt, 0);
    return () => clearTimeout(timer);
  }, [requestedAt, start, pathname]);

  // Closing happens once per run. Finishing a tour reports both "finished"
  // and "closed", and a press outside can land in the same moment: each used
  // to mark the tour seen and hand the request back again.
  const runningRef = useRef(running);
  useEffect(() => {
    runningRef.current = running;
  }, [running]);
  const handleClose = useCallback(() => {
    const ended = runningRef.current;
    if (!ended) return;
    runningRef.current = null;
    complete(ended.pathname);
    setRunning(null);
    onCloseRef.current();
  }, [complete]);

  const open = running !== null && running.pathname === pathname;

  // Left the page with the tour still up (a link inside the spotlight): it
  // ends there, counted as seen for the page it ran on.
  useEffect(() => {
    if (running && running.pathname !== pathname) handleClose();
  }, [running, pathname, handleClose]);

  // UI-02: a press outside the tour's card ends the tour and goes through to
  // the control under it, so no click is ever spent on dismissing a tour.
  useEffect(() => {
    if (!open) return;
    const onPress = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(".ant-tour")) return;
      handleClose();
    };
    window.addEventListener("pointerdown", onPress, true);
    return () => window.removeEventListener("pointerdown", onPress, true);
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <Tour
      open
      rootClassName="product-tour"
      steps={running.steps}
      onClose={handleClose}
      onFinish={handleClose}
      indicatorsRender={(current, total) => (
        <span>
          {current + 1} of {total}
        </span>
      )}
    />
  );
}


export default ProductTour;
